/**
 * DEVELOPER DOCUMENTATION
 *
 * Include your custom JavaScript here.
 *
 * The theme Focal has been developed to be easily extensible through the usage of a lot of different JavaScript
 * events, as well as the usage of custom elements (https://developers.google.com/web/fundamentals/web-components/customelements)
 * to easily extend the theme and re-use the theme infrastructure for your own code.
 *
 * The technical documentation is summarized here.
 *
 * ------------------------------------------------------------------------------------------------------------
 * BEING NOTIFIED WHEN A VARIANT HAS CHANGED
 * ------------------------------------------------------------------------------------------------------------
 *
 * This event is fired whenever a the user has changed the variant in a selector. The target get you the form
 * that triggered this event.
 *
 * Example:
 *
 * document.addEventListener('variant:changed', function(event) {
 *   let variant = event.detail.variant; // Gives you access to the whole variant details
 *   let form = event.target;
 * });
 *
 * ------------------------------------------------------------------------------------------------------------
 * BEING NOTIFIED WHEN A NEW VARIANT IS ADDED TO THE CART
 * ------------------------------------------------------------------------------------------------------------
 *
 * This event is fired whenever a variant is added to the cart through a form selector (product page, quick
 * view...). This event DOES NOT include any change done through the cart on an existing variant. For that,
 * please refer to the "cart:updated" event.
 *
 * Example:
 *
 * document.addEventListener('variant:added', function(event) {
 *   var variant = event.detail.variant; // Get the variant that was added
 * });
 *
 * ------------------------------------------------------------------------------------------------------------
 * BEING NOTIFIED WHEN THE CART CONTENT HAS CHANGED
 * ------------------------------------------------------------------------------------------------------------
 *
 * This event is fired whenever the cart content has changed (if the quantity of a variant has changed, if a variant
 * has been removed, if the note has changed...). This event will also be emitted when a new variant has been
 * added (so you will receive both "variant:added" and "cart:updated"). Contrary to the variant:added event,
 * this event will give you the complete details of the cart.
 *
 * Example:
 *
 * document.addEventListener('cart:updated', function(event) {
 *   var cart = event.detail.cart; // Get the updated content of the cart
 * });
 *
 * ------------------------------------------------------------------------------------------------------------
 * REFRESH THE CART/MINI-CART
 * ------------------------------------------------------------------------------------------------------------
 *
 * If you are adding variants to the cart and would like to instruct the theme to re-render the cart, you cart
 * send the cart:refresh event, as shown below:
 *
 * document.documentElement.dispatchEvent(new CustomEvent('cart:refresh', {
 *   bubbles: true
 * }));
 *
 * ------------------------------------------------------------------------------------------------------------
 * USAGE OF CUSTOM ELEMENTS
 * ------------------------------------------------------------------------------------------------------------
 *
 * Our theme makes extensive use of HTML custom elements. Custom elements are an awesome way to extend HTML
 * by creating new elements that carry their own JavaScript for adding new behavior. The theme uses a large
 * number of custom elements, but the two most useful are drawer and popover. Each of those components add
 * a "open" attribute that you can toggle on and off. For instance, let's say you would like to open the cart
 * drawer, whose id is "mini-cart", you simply need to retrieve it and set its "open" attribute to true (or
 * false to close it):
 *
 * document.getElementById('mini-cart').open = true;
 *
 * Thanks to the power of custom elements, the theme will take care automagically of trapping focus, maintaining
 * proper accessibility attributes...
 *
 * If you would like to create your own drawer, you can re-use the <drawer-content> content. Here is a simple
 * example:
 *
 * // Make sure you add "aria-controls", "aria-expanded" and "is" HTML attributes to your button:
 * <button type="button" is="toggle-button" aria-controls="id-of-drawer" aria-expanded="false">Open drawer</button>
 *
 * <drawer-content id="id-of-drawer">
 *   Your content
 * </drawer-content>
 *
 * The nice thing with custom elements is that you do not actually need to instantiate JavaScript yourself: this
 * is done automatically as soon as the element is inserted to the DOM.
 *
 * ------------------------------------------------------------------------------------------------------------
 * THEME DEPENDENCIES
 * ------------------------------------------------------------------------------------------------------------
 *
 * While the theme tries to keep outside dependencies as small as possible, the theme still uses third-party code
 * to power some of its features. Here is the list of all dependencies:
 *
 * "vendor.js":
 *
 * The vendor.js contains required dependencies. This file is loaded in parallel of the theme file.
 *
 * - custom-elements polyfill (used for built-in elements on Safari - v1.0.0): https://github.com/ungap/custom-elements
 * - web-animations-polyfill (used for polyfilling WebAnimations on Safari 12, this polyfill will be removed in 1 year - v2.3.2): https://github.com/web-animations/web-animations-js
 * - instant-page (v5.1.0): https://github.com/instantpage/instant.page
 * - tocca (v2.0.9); https://github.com/GianlucaGuarini/Tocca.js/
 * - seamless-scroll-polyfill (v2.0.0): https://github.com/magic-akari/seamless-scroll-polyfill
 *
 * "flickity.js": v2.2.0 (with the "fade" package). Flickity is only loaded on demand if there is a product image
 * carousel on the page. Otherwise it is not loaded.
 *
 * "photoswipe": v4.1.3. PhotoSwipe is only loaded on demand to power the zoom feature on product page. If the zoom
 * feature is disabled, then this script is never loaded.
 */

/**
 * ------------------------------------------------------------------------------------------------------------
 * FLAVOR SWITCHER ARROWS
 * ------------------------------------------------------------------------------------------------------------
 *
 * Shows the scroll arrows on the flavor cards only when the track actually overflows, and hides whichever arrow
 * points at an end that has already been reached.
 *
 * Selecting a flavor is not handled here. The cards are ordinary option radios bound to the product form, so
 * <variant-picker> drives the variant change exactly as it does for the theme's built in selectors. Everything below
 * is delegated from the document because a variant change replaces the variant picker markup wholesale, which would
 * throw away any listener attached directly to these elements.
 */
(() => {
  // The pack size cards reuse this behaviour, so every selector below accepts their data-pack-* twins too.
  const SWITCHER_SELECTOR = '[data-flavor-switcher], [data-pack-switcher]';
  const VIEWPORT_SELECTOR = '[data-flavor-viewport], [data-pack-viewport]';
  const NAV_SELECTOR = '[data-flavor-nav], [data-pack-nav]';

  const updateArrows = (switcher) => {
    const viewport = switcher?.querySelector(VIEWPORT_SELECTOR);

    if (!viewport) {
      return;
    }

    const overflow = viewport.scrollWidth - viewport.clientWidth;
    const fits = overflow <= 2;

    switcher
      .querySelector('[data-flavor-nav="prev"], [data-pack-nav="prev"]')
      ?.classList.toggle('is-hidden', fits || viewport.scrollLeft <= 1);

    switcher
      .querySelector('[data-flavor-nav="next"], [data-pack-nav="next"]')
      ?.classList.toggle('is-hidden', fits || viewport.scrollLeft >= overflow - 1);
  };

  const updateAll = () => document.querySelectorAll(SWITCHER_SELECTOR).forEach(updateArrows);

  document.addEventListener('click', (event) => {
    const nav = event.target.closest?.(NAV_SELECTOR);

    if (!nav) {
      return;
    }

    const viewport = nav.closest(SWITCHER_SELECTOR)?.querySelector(VIEWPORT_SELECTOR);

    if (!viewport) {
      return;
    }

    const distance = Math.max(viewport.clientWidth * 0.8, 120);

    viewport.scrollBy({ left: (nav.dataset.flavorNav || nav.dataset.packNav) === 'prev' ? -distance : distance, behavior: 'smooth' });
  });

  // Scroll events do not bubble, so the listener has to run in the capture phase to catch them by delegation
  document.addEventListener(
    'scroll',
    (event) => {
      if (event.target?.matches?.(VIEWPORT_SELECTOR)) {
        updateArrows(event.target.closest(SWITCHER_SELECTOR));
      }
    },
    true
  );

  window.addEventListener('resize', updateAll);

  // Images settle after first paint and change scrollWidth, and a variant change re-renders the whole picker
  window.addEventListener('load', updateAll);
  document.addEventListener('variant:changed', () => requestAnimationFrame(updateAll));

  updateAll();
})();

/**
 * ------------------------------------------------------------------------------------------------------------
 * PRODUCT PAGE FREE SHIPPING BAR TEXT
 * ------------------------------------------------------------------------------------------------------------
 *
 * The theme's <free-shipping-bar> updates its --progress variable on cart:updated and nothing else, so the bar fills
 * but the sentence above it keeps whatever Liquid rendered on page load. In the cart drawer that goes unnoticed,
 * because every cart mutation pulls a freshly rendered "mini-cart" section from the server and replaces the markup
 * wholesale. Nothing re-renders the product section, so on the product page the wording would sit frozen while the
 * bar moved underneath it.
 *
 * This fills that gap. Both sentences come from Liquid as data attributes carrying an @amount@ placeholder, so no
 * German lives in here, and formatMoney below is a port of the theme's own helper (js/helper/currency.js, bundled and
 * not reachable from this file) so the product page and the cart can never format the same number differently.
 */
(() => {
  const formatMoney = (cents, format) => {
    const formatString = format || window.themeVariables?.settings?.moneyFormat || '';
    const placeholder = /\{\{\s*(\w+)\s*\}\}/;
    const match = formatString.match(placeholder);

    if (!match) {
      return '';
    }

    const withDelimiters = (precision, thousands = ',', decimal = '.') => {
      if (isNaN(cents) || cents == null) {
        return '0';
      }

      const parts = (cents / 100).toFixed(precision).split('.');
      const whole = parts[0].replace(/(\d)(?=(\d\d\d)+(?!\d))/g, `$1${thousands}`);

      return whole + (parts[1] ? decimal + parts[1] : '');
    };

    let value = '';

    switch (match[1]) {
      case 'amount':
        value = withDelimiters(2);
        break;
      case 'amount_no_decimals':
        value = withDelimiters(0);
        break;
      case 'amount_with_space_separator':
        value = withDelimiters(2, ' ', '.');
        break;
      case 'amount_with_comma_separator':
        value = withDelimiters(2, '.', ',');
        break;
      case 'amount_with_apostrophe_separator':
        value = withDelimiters(2, "'", '.');
        break;
      case 'amount_no_decimals_with_comma_separator':
        value = withDelimiters(0, '.', ',');
        break;
      case 'amount_no_decimals_with_space_separator':
        value = withDelimiters(0, ' ');
        break;
      case 'amount_no_decimals_with_apostrophe_separator':
        value = withDelimiters(0, "'");
        break;
      default:
        return '';
    }

    return formatString.replace(placeholder, value);
  };

  const refresh = (event) => {
    const bars = document.querySelectorAll('[data-pdp-shipping-bar]');

    if (bars.length === 0) {
      return;
    }

    const items = event.detail?.cart?.items;

    // cart:refresh is sometimes dispatched with no detail at all, so bail rather than render a wrong number
    if (!Array.isArray(items)) {
      return;
    }

    // Same basis the Liquid uses: shippable line items only, minus cart level discounts
    const shippable = items
      .filter((item) => item.requires_shipping)
      .reduce((sum, item) => sum + item.final_line_price, 0);

    const discounts = (event.detail.cart.cart_level_discount_applications || []).reduce(
      (sum, discount) => sum + discount.total_allocated_amount,
      0
    );

    const total = shippable - discounts;

    bars.forEach((bar) => {
      const target = bar.querySelector('[data-shipping-bar-text]');
      const threshold = parseFloat(bar.dataset.threshold);

      if (!target || !threshold) {
        return;
      }

      if (total >= threshold) {
        target.textContent = bar.dataset.successText || '';
        return;
      }

      const remaining = formatMoney(threshold - total);

      // Leave the server rendered wording alone rather than show a broken sentence
      if (remaining) {
        // Same wrapper the Liquid uses, so the amount stays bold after a cart update
        const amount = `<strong class="text--strong">${remaining}</strong>`;

        target.innerHTML = (bar.dataset.remainingTemplate || '').replace('@amount@', amount);
      }
    });
  };

  // cart:updated covers add to cart and drawer quantity changes. cart:refresh additionally covers the back/forward
  // restore in js-variables.liquid, where the cart may have moved on in another tab while this page sat in bfcache.
  document.documentElement.addEventListener('cart:updated', refresh);
  document.documentElement.addEventListener('cart:refresh', refresh);
})();

/**
 * ------------------------------------------------------------------------------------------------------------
 * PRODUCT TABS
 * ------------------------------------------------------------------------------------------------------------
 *
 * One set of markup serving two presentations: tabs above 1000px, accordions below. A panel carries "is-active" for
 * the desktop tab bar and "is-open" for the mobile accordion, and each breakpoint's CSS only honours the state it
 * cares about. That means neither handler has to know which breakpoint is currently active, and no resize listener is
 * needed to keep them in sync.
 */
(() => {
  document.addEventListener('click', (event) => {
    const tab = event.target.closest?.('[data-tabs-tab]');

    if (tab) {
      const root = tab.closest('[data-product-tabs]');
      const selected = tab.dataset.tabsTab;

      root?.querySelectorAll('[data-tabs-tab]').forEach((item) => {
        const isActive = item === tab;

        item.classList.toggle('is-active', isActive);
        item.setAttribute('aria-selected', isActive ? 'true' : 'false');
      });

      root?.querySelectorAll('[data-tabs-panel]').forEach((panel) => {
        panel.classList.toggle('is-active', panel.dataset.tabsPanel === selected);
      });

      return;
    }

    const accordion = event.target.closest?.('[data-tabs-accordion]');

    if (accordion) {
      const panel = accordion.closest('[data-tabs-panel]');

      accordion.setAttribute('aria-expanded', panel.classList.toggle('is-open') ? 'true' : 'false');
    }
  });
})();
/**
 * ------------------------------------------------------------------------------------------------------------
 * MEGA MENU CARET
 * ------------------------------------------------------------------------------------------------------------
 *
 * Points the caret of the product worlds mega menu at the navigation item that opened it.
 *
 * The panel is absolutely positioned against the whole header so that it can span its full width, which leaves CSS
 * no way to reach back to the horizontal position of the trigger. Making the list item a positioning context would
 * fix the caret but shrink the panel down to the width of the link, so the offset is measured here instead and
 * handed to CSS as a custom property. Until this runs the caret falls back to the centre of the panel.
 */
(() => {
  const PANEL_SELECTOR = '.mega-menu--products:not([hidden])';

  const positionCarets = () => {
    document.querySelectorAll(PANEL_SELECTOR).forEach((panel) => {
      const trigger = panel.closest('.header__linklist-item')?.querySelector('.header__linklist-link');

      if (!trigger) {
        return;
      }

      const triggerRect = trigger.getBoundingClientRect();
      const panelRect = panel.getBoundingClientRect();

      panel.style.setProperty('--mmp-caret-x', `${triggerRect.left - panelRect.left + triggerRect.width / 2}px`);
    });
  };

  // The theme unhides the panel from inside a timeout, so measure on the next frame rather than during the event
  document.addEventListener('desktop-nav:dropdown:open', () => requestAnimationFrame(positionCarets));
  window.addEventListener('resize', positionCarets);
})();

/**
 * ------------------------------------------------------------------------------------------------------------
 * SLIDER NAVIGATION DOTS
 * ------------------------------------------------------------------------------------------------------------
 *
 * Pagination dots for the free scrolling sliders (featured collections, multi-column), plus a scroll driven
 * refresh of the disabled state on the theme prev/next arrows.
 *
 * Three things make this less trivial than it looks.
 *
 * Which element scrolls depends on the viewport. From 1000px up the inner grid is the scroll container (it uses
 * overflow:hidden on multi-column, which still scrolls programmatically); below that the inner grid is given
 * min-width:min-content so it stops overflowing and the outer .scroller wrapper takes over. The container is
 * therefore resolved on every measurement instead of being cached once.
 *
 * The number of dots cannot be known to Liquid. One dot is one viewport worth of scrolling, and that count moves
 * with the breakpoint and with the column size settings, so the buttons are built here and rebuilt only when the
 * count actually changes. Liquid renders nothing but the empty container, which is what keeps the section
 * settings in charge of whether dots exist at all.
 *
 * The theme arrows recompute their disabled state inside their own click handler and never listen to scroll, so
 * scrolling to either end by hand used to leave an arrow enabled. They are refreshed here once scrolling settles
 * rather than on every frame: the theme handler predicts the end of the track when it starts a smooth scroll, and
 * correcting it mid flight would blink the arrow back on and off again.
 */
(() => {
  const CONTAINER_SELECTOR = '[data-slider-dots]';
  // data-slider-root lets any markup opt in; the two element names are the sections this started life on
  const ROOT_SELECTOR = '[data-slider-root], multi-column, product-list';
  const INNER_SELECTOR = '[data-slider-scroller], .multi-column__inner, .product-list__inner';
  const SETTLE_DELAY = 150;

  const initialised = new WeakSet();
  const refreshers = new Set();

  const flip = () => (window.themeVariables?.settings?.direction === 'rtl' ? -1 : 1);

  const findScroller = (root) => {
    const inner = root.querySelector(INNER_SELECTOR);

    if (!inner) {
      return null;
    }

    if (inner.scrollWidth - inner.clientWidth > 1) {
      return inner;
    }

    const outer = inner.closest('.scroller');

    return outer && outer.scrollWidth - outer.clientWidth > 1 ? outer : null;
  };

  const render = (container, count) => {
    const template = container.dataset.sliderDots || 'Go to slide @index@';
    const fragment = document.createDocumentFragment();

    for (let index = 0; index < count; index += 1) {
      const button = document.createElement('button');

      button.type = 'button';
      button.className = 'dots-nav__item tap-area';
      button.setAttribute('aria-current', index === 0 ? 'true' : 'false');

      const label = document.createElement('span');

      label.className = 'visually-hidden';
      label.textContent = template.replace('@index@', index + 1);

      button.appendChild(label);
      fragment.appendChild(button);
    }

    container.replaceChildren(fragment);
  };

  const sync = (container, root, includeArrows) => {
    if (!container.isConnected) {
      return;
    }

    const scroller = findScroller(root);

    // A collection tab that is not the visible one measures zero, so bail rather than divide by it
    if (!scroller || scroller.clientWidth === 0) {
      container.hidden = true;
      root.dataset.sliderScrollable = 'false';
      return;
    }

    root.dataset.sliderScrollable = 'true';

    const page = scroller.clientWidth;
    const max = scroller.scrollWidth - page;
    const count = Math.ceil((max - 1) / page) + 1;

    // Rebuilding on every scroll would throw away the button the pointer is currently on
    if (container.childElementCount !== count) {
      render(container, count);
    }

    container.hidden = false;

    const position = scroller.scrollLeft * flip();
    // The final page is usually a partial one, so it never rounds up to its own index
    const index = position >= max - 1 ? count - 1 : Math.round(position / page);

    Array.from(container.children).forEach((button, buttonIndex) => {
      button.setAttribute('aria-current', buttonIndex === index ? 'true' : 'false');
    });

    if (!includeArrows) {
      return;
    }

    // Set disabled on the buttons rather than through the prev-next-buttons setters: if that element has not
    // been upgraded yet, assigning the property would shadow the setter and silently stop working afterwards
    const previous = root.querySelector('.prev-next-button--prev');
    const next = root.querySelector('.prev-next-button--next');

    if (previous) {
      previous.disabled = position <= 1;
    }

    if (next) {
      next.disabled = position >= max - 1;
    }
  };

  const setup = (container) => {
    if (initialised.has(container)) {
      return;
    }

    const root = container.closest(ROOT_SELECTOR);
    const inner = root?.querySelector(INNER_SELECTOR);

    if (!inner) {
      return;
    }

    initialised.add(container);

    let frame = null;
    let settleTimer = null;
    let pendingArrows = false;

    const refresh = (includeArrows) => {
      pendingArrows = pendingArrows || includeArrows;

      if (frame === null) {
        frame = requestAnimationFrame(() => {
          frame = null;

          const withArrows = pendingArrows;

          pendingArrows = false;
          sync(container, root, withArrows);
        });
      }
    };

    const onScroll = () => {
      refresh(false);
      clearTimeout(settleTimer);
      settleTimer = setTimeout(() => sync(container, root, true), SETTLE_DELAY);
    };

    const scrollTo = (position) => {
      const scroller = findScroller(root);

      if (!scroller) {
        return;
      }

      const max = scroller.scrollWidth - scroller.clientWidth;

      scroller.scrollTo({ left: Math.max(0, Math.min(position, max)) * flip(), behavior: 'smooth' });
    };

    container.addEventListener('click', (event) => {
      const button = event.target.closest('button');
      const scroller = findScroller(root);

      if (!button || !scroller) {
        return;
      }

      scrollTo(Array.from(container.children).indexOf(button) * scroller.clientWidth);
    });

    // MultiColumn and ProductList already answer these events for their own markup, so stepping them here as well
    // would scroll twice. Anything that opted in with data-slider-root has nobody else listening.
    if (root.matches('[data-slider-root]')) {
      const step = (direction) => {
        const scroller = findScroller(root);

        if (scroller) {
          scrollTo(scroller.scrollLeft * flip() + direction * scroller.clientWidth);
        }
      };

      root.addEventListener('prev-next:prev', () => step(-1));
      root.addEventListener('prev-next:next', () => step(1));
    }

    // Only one of the two ever scrolls at a given breakpoint, so listening to both costs nothing
    [inner, inner.closest('.scroller')].forEach((element) => {
      element?.addEventListener('scroll', onScroll, { passive: true });
    });

    // Covers the breakpoint handover, a hidden collection tab being shown, and late layout shifts
    new ResizeObserver(() => refresh(true)).observe(inner);

    refreshers.add(() => refresh(true));
    refresh(true);
  };

  const init = () => document.querySelectorAll(CONTAINER_SELECTOR).forEach(setup);
  const refreshAll = () => refreshers.forEach((refresh) => refresh());

  init();

  window.addEventListener('load', () => {
    init();
    refreshAll();
  });

  window.addEventListener('resize', refreshAll);
  document.addEventListener('shopify:section:load', init);
})();
